/**
 * AgentInboxTab Component
 * AIDLC Administrative Backend
 *
 * Displays queued prompts for an agent in an email inbox style.
 * Shows subject, from, priority, status, and preview of prompt content.
 * Includes Compose Email panel and Sent History for agent email identity.
 */

import { useState, useEffect, useCallback } from 'react';
import { getAgentInbox } from '../../services/agentAssetService';
import { sendAgentEmail, getSentEmails, getTemplates } from '../../services/emailTemplateService';
import { getSignaturePreview } from '../../services/emailService';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { Badge } from '../shared/Badge';
import type { PromptQueueItem, PromptStatus, PromptPriority } from '../../types/chat';
import type { SentEmailRecord, EmailTemplate } from '../../types/email';

/** The only mailbox the portal sends from (KD-1 E4). */
const PORTAL_SENDER = 'agents@ldaja.com';

interface Props {
  agentId: string;
  agentName: string;
  agentEmail?: string;
}

const STATUS_BADGE: Record<PromptStatus, { variant: 'warning' | 'info' | 'success' | 'danger'; label: string }> = {
  pending: { variant: 'warning', label: 'Pending' },
  processing: { variant: 'info', label: 'Processing' },
  completed: { variant: 'success', label: 'Completed' },
  failed: { variant: 'danger', label: 'Failed' },
};

const PRIORITY_STYLES: Record<PromptPriority, { color: string; label: string }> = {
  urgent: { color: 'text-red-600', label: '!!!' },
  high: { color: 'text-orange-500', label: '!!' },
  normal: { color: 'text-gray-400', label: '!' },
  low: { color: 'text-gray-300', label: '-' },
};

type FilterStatus = 'all' | PromptStatus;
type TabView = 'inbox' | 'compose' | 'sent';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AgentInboxTab({ agentId, agentName }: Props) {
  const [prompts, setPrompts] = useState<PromptQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tabView, setTabView] = useState<TabView>('inbox');

  // Compose state
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeTemplateId, setComposeTemplateId] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);
  const [signatureHtml, setSignatureHtml] = useState('');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);

  // Sent history state
  const [sentEmails, setSentEmails] = useState<SentEmailRecord[]>([]);
  const [sentLoading, setSentLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAgentInbox(agentId);
      setPrompts(data);
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  // Load signature preview when compose tab opens
  useEffect(() => {
    if (tabView === 'compose') {
      // Every portal email is sent as agents@ldaja.com (KD-1 E4), so preview that signature, not the agent's own.
      getSignaturePreview(PORTAL_SENDER).then(setSignatureHtml).catch(() => {});
      getTemplates().then(setTemplates).catch(() => {});
    }
  }, [tabView]);

  // Load sent history when sent tab opens
  useEffect(() => {
    if (tabView === 'sent') {
      setSentLoading(true);
      getSentEmails(agentId).then(setSentEmails).catch(() => {}).finally(() => setSentLoading(false));
    }
  }, [tabView, agentId]);

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    setComposeTemplateId(templateId);
    if (templateId) {
      const tmpl = templates.find(t => t.id === templateId);
      if (tmpl) {
        setComposeSubject(tmpl.subjectTemplate);
        setComposeBody(tmpl.bodyTemplate);
      }
    }
  };

  // Send email
  const handleSend = async () => {
    if (!composeTo || !EMAIL_REGEX.test(composeTo)) {
      setSendResult({ success: false, message: 'Please enter a valid email address.' });
      return;
    }
    if (!composeSubject.trim()) {
      setSendResult({ success: false, message: 'Subject is required.' });
      return;
    }
    if (!composeBody.trim()) {
      setSendResult({ success: false, message: 'Message body is required.' });
      return;
    }

    setSending(true);
    setSendResult(null);
    try {
      await sendAgentEmail({
        to: composeTo,
        subject: composeSubject,
        body: composeBody,
        agentId,
        templateId: composeTemplateId || undefined,
      });
      setSendResult({ success: true, message: `Email sent to ${composeTo}` });
      setComposeTo('');
      setComposeSubject('');
      setComposeBody('');
      setComposeTemplateId('');
    } catch (err) {
      setSendResult({ success: false, message: (err as Error).message || 'Failed to send email' });
    } finally {
      setSending(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading inbox..." size="sm" />;

  const filtered = filter === 'all' ? prompts : prompts.filter(p => p.status === filter);
  const selected = selectedId ? prompts.find(p => p.id === selectedId) : null;

  const pendingCount = prompts.filter(p => p.status === 'pending').length;
  const processingCount = prompts.filter(p => p.status === 'processing').length;

  return (
    <div className="space-y-4">
      {/* Tab bar: Inbox / Compose / Sent */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {([
            { id: 'inbox' as TabView, label: 'Inbox', icon: (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            )},
            { id: 'compose' as TabView, label: 'Compose', icon: (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            )},
            { id: 'sent' as TabView, label: 'Sent', icon: (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )},
          ]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setTabView(tab.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5 transition-colors ${
                tabView === tab.id
                  ? 'bg-ldaja-blue text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.id === 'inbox' && pendingCount > 0 && (
                <span className="px-1 py-0.5 bg-white/20 text-[10px] rounded-full font-bold">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>
        {tabView === 'inbox' && (
          <button
            onClick={load}
            className="text-xs text-gray-500 hover:text-ldaja-blue transition-colors flex items-center gap-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        )}
      </div>

      {/* ─── COMPOSE VIEW ─── */}
      {tabView === 'compose' && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-4 h-4 text-ldaja-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <h3 className="text-sm font-semibold text-ldaja-black">
              Compose an email from {agentName}
            </h3>
            <span className="text-[10px] text-gray-400">Sends as LDAJA Agents ({PORTAL_SENDER})</span>
          </div>

          {/* Template selector */}
          {templates.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Template (optional)</label>
              <select
                value={composeTemplateId}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-ldaja-blue"
              >
                <option value="">-- No template --</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
                ))}
              </select>
            </div>
          )}

          {/* To */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input
              type="email"
              value={composeTo}
              onChange={(e) => setComposeTo(e.target.value)}
              placeholder="recipient@example.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-ldaja-blue"
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
            <input
              type="text"
              value={composeSubject}
              onChange={(e) => setComposeSubject(e.target.value)}
              placeholder="Email subject"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-ldaja-blue"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
            <textarea
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              placeholder="Type your message..."
              rows={8}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-ldaja-blue resize-y"
            />
          </div>

          {/* Signature Preview */}
          {signatureHtml && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Signature Preview</label>
              <div
                className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm"
                dangerouslySetInnerHTML={{ __html: signatureHtml }}
              />
            </div>
          )}

          {/* Send result message */}
          {sendResult && (
            <div className={`p-3 rounded-lg text-sm ${
              sendResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {sendResult.message}
            </div>
          )}

          {/* Send button */}
          <div className="flex justify-end">
            <button
              onClick={handleSend}
              disabled={sending}
              className="px-4 py-2 bg-ldaja-blue text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {sending ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send from {agentName}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ─── SENT HISTORY VIEW ─── */}
      {tabView === 'sent' && (
        <div>
          {sentLoading ? (
            <LoadingSpinner message="Loading sent history..." size="sm" />
          ) : sentEmails.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              <p className="text-sm text-gray-500">No sent emails yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Emails sent as {agentName} will appear here.
              </p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
              {sentEmails.map(email => (
                <div key={email.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-ldaja-black truncate">{email.subject}</span>
                        {email.success ? (
                          <Badge variant="success">Sent</Badge>
                        ) : (
                          <Badge variant="danger">Failed</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        To: {email.to.join(', ')}
                      </p>
                      <p className="text-[11px] text-gray-400 truncate mt-0.5">{email.bodyPreview}</p>
                      {email.error && (
                        <p className="text-[11px] text-red-500 mt-0.5">{email.error}</p>
                      )}
                    </div>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                      {formatInboxDate(email.sentAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── INBOX VIEW ─── */}
      {tabView === 'inbox' && (
        <>
          {/* Filter tabs */}
          <div className="flex gap-1 border-b border-gray-200 pb-0">
            {([
              { id: 'all' as FilterStatus, label: 'All', count: prompts.length },
              { id: 'pending' as FilterStatus, label: 'Pending', count: pendingCount },
              { id: 'processing' as FilterStatus, label: 'In Progress', count: processingCount },
              { id: 'completed' as FilterStatus, label: 'Completed', count: prompts.filter(p => p.status === 'completed').length },
            ]).map(tab => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                  filter === tab.id
                    ? 'border-ldaja-blue text-ldaja-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-1 text-[10px] text-gray-400">({tab.count})</span>
                )}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-gray-500">
                {filter === 'all' ? 'No prompts in inbox' : `No ${filter} prompts`}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Prompts queued for {agentName} will appear here.
              </p>
            </div>
          ) : (
            <div className="flex gap-0 border border-gray-200 rounded-lg overflow-hidden" style={{ minHeight: 320 }}>
              {/* Message list (left side) */}
              <div className={`${selected ? 'w-2/5' : 'w-full'} border-r border-gray-200 overflow-y-auto divide-y divide-gray-100`}>
                {filtered.map(prompt => {
                  const isSelected = selectedId === prompt.id;
                  const isPending = prompt.status === 'pending';
                  const priorityStyle = PRIORITY_STYLES[prompt.priority] || PRIORITY_STYLES.normal;

                  return (
                    <button
                      key={prompt.id}
                      onClick={() => setSelectedId(isSelected ? null : prompt.id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        isSelected ? 'bg-ldaja-blue/5 border-l-2 border-ldaja-blue' : 'hover:bg-gray-50 border-l-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-xs font-bold ${priorityStyle.color}`}>{priorityStyle.label}</span>
                            <span className={`text-sm truncate ${isPending ? 'font-semibold text-ldaja-black' : 'font-normal text-gray-700'}`}>
                              {prompt.subject}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-gray-400">from: {prompt.from}</span>
                            <Badge variant={STATUS_BADGE[prompt.status].variant}>
                              {STATUS_BADGE[prompt.status].label}
                            </Badge>
                          </div>
                          <p className="text-[11px] text-gray-400 truncate mt-0.5">{prompt.prompt}</p>
                        </div>
                        <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                          {formatInboxDate(prompt.createdAt)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Reading pane (right side) */}
              {selected && (
                <div className="w-3/5 overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                    <h4 className="text-base font-semibold text-ldaja-black">{selected.subject}</h4>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                      <span>From: <span className="font-medium text-gray-700">{selected.from}</span></span>
                      <span>Priority: <span className={`font-medium ${PRIORITY_STYLES[selected.priority].color}`}>{selected.priority}</span></span>
                      <Badge variant={STATUS_BADGE[selected.status].variant}>
                        {STATUS_BADGE[selected.status].label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-[10px] text-gray-400">
                      <span>Queued: {new Date(selected.createdAt).toLocaleString()}</span>
                      {selected.processedAt && <span>Started: {new Date(selected.processedAt).toLocaleString()}</span>}
                      {selected.completedAt && <span>Completed: {new Date(selected.completedAt).toLocaleString()}</span>}
                    </div>
                  </div>

                  {/* Prompt content */}
                  <div className="px-6 py-4">
                    {selected.personaContext && (
                      <div className="mb-3 text-xs">
                        <span className="font-semibold text-gray-600">Persona Context:</span>
                        <p className="text-gray-500 mt-0.5">{selected.personaContext}</p>
                      </div>
                    )}
                    {selected.projectContext && (
                      <div className="mb-3 text-xs">
                        <span className="font-semibold text-gray-600">Project Context:</span>
                        <p className="text-gray-500 mt-0.5">{selected.projectContext}</p>
                      </div>
                    )}
                    <div className="mb-3">
                      <span className="text-xs font-semibold text-gray-600">Prompt:</span>
                      <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{selected.prompt}</pre>
                      </div>
                    </div>
                    {selected.result && (
                      <div className="mt-4">
                        <span className="text-xs font-semibold text-gray-600">Result:</span>
                        <div className="mt-1 p-3 bg-green-50 rounded-lg border border-green-200">
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{selected.result}</pre>
                        </div>
                      </div>
                    )}
                    {selected.tokensUsed && (
                      <div className="mt-3 text-[10px] text-gray-400">
                        Tokens used: {selected.tokensUsed.toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatInboxDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
